import RocketBlock from '@/assets/icons/RocketBlock';
import MarketingCard from '@/components/Marketplace/market-card';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Command, CommandInput } from '@/components/ui/command';
import LayoutInside from '@/layout/LayoutInside';
import useModalStore from '@/store/useModalStore';
import { ListFilter, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';

import FireFlameBlock from '@/assets/icons/FireFlameBlock';
import SpaarklesBlock from '@/assets/icons/SpaarklesBlock';
import MarketFilter from '@/components/Marketplace/market-filter';
import Divider from '@/components/shared/Divider/Divider';
import { EmptyItem } from '@/components/shared/EmptyItem/EmptyItem';
import CustomSheet from '@/components/shared/Sheet/Sheet';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { MarketingService } from '@/services/marketplace.service';
import { useQuery } from '@tanstack/react-query';
import { parseAsArrayOf, parseAsString, useQueryState } from 'nuqs';
import { Navigation } from 'swiper/modules';
import {
  convertToVisualParams
} from '@/hooks/useSales';
import type { MarketplaceProductListItem } from '@/services/marketplace.service';

type MarketplaceFilterKey =
  | 'category_type'
  | 'category_id'
  | 'currency'
  | 'commission_range'
  | 'price';

type MarketplaceFilterParams = {
  category_type: string;
  category_id: string[];
  currency: string;
  commission_range: number[];
  price: number[];
};

type FilterBadgeLabelKey = 'all' | 'digital' | 'pharmaceutical';

type FilterBadge = {
  key: string;
  group: string;
  label: string;
  onClick: () => void;
};

const FILTER_KEYS: MarketplaceFilterKey[] = [
  'category_type',
  'category_id',
  'currency',
  'commission_range',
  'price',
];

function isFilterKey(key: string): key is MarketplaceFilterKey {
  return FILTER_KEYS.includes(key as MarketplaceFilterKey);
}

export default function Marketplace() {
  const items = [
    {
      text: 'Marketplace',
      link: '/marketplace',
    },
  ];

  const { t } = useTranslation(['common', 'sales']);
  const { modals, openModal, closeModal } = useModalStore();
  const [searchQuery, setSearchQuery] = useQueryState('q', parseAsString);
  const [localSearchValue, setLocalSearchValue] = useState(searchQuery || '');

  // Sincroniza o valor local quando a URL muda (ex: ao limpar filtros)
  useEffect(() => {
    setLocalSearchValue(searchQuery || '');
  }, [searchQuery]);

  const [categoryType, setCategoryType] = useQueryState(
    'category_type',
    parseAsString
  );
  const [categoryId, setCategoryId] = useQueryState(
    'category_id',
    parseAsArrayOf(parseAsString)
  );
  const [currency, setCurrency] = useQueryState('currency', parseAsString);
  const [commissionRange, setCommissionRange] = useQueryState(
    'commission_range',
    parseAsArrayOf(parseAsString)
  );
  const [price, setPrice] = useQueryState(
    'price',
    parseAsArrayOf(parseAsString)
  );

  const filterParams = useMemo((): MarketplaceFilterParams => {
    return {
      category_type: categoryType || '',
      category_id: Array.isArray(categoryId) && categoryId.length > 0
        ? categoryId
        : categoryId && !Array.isArray(categoryId)
        ? [categoryId]
        : [],
      currency: currency || '',
      commission_range: Array.isArray(commissionRange) && commissionRange.length > 0
        ? commissionRange.map((v: string) => Number(v))
        : commissionRange && !Array.isArray(commissionRange)
        ? [Number(commissionRange)]
        : [0, 100],
      price: Array.isArray(price) && price.length > 0
        ? price.map((v: string) => Number(v))
        : price && !Array.isArray(price)
        ? [Number(price)]
        : [0, 3000],
    };
  }, [categoryType, categoryId, currency, commissionRange, price]);

  const setFilterParams = useCallback(
    (
      params:
        | MarketplaceFilterParams
        | ((current: MarketplaceFilterParams) => MarketplaceFilterParams),
    ) => {
      const newParams =
        typeof params === 'function' ? params(filterParams) : params;

      if (newParams?.category_id && Array.isArray(newParams.category_id) && newParams.category_id.length > 0) {
        setCategoryId(newParams.category_id);
      } else {
        setCategoryId(null);
      }

      if (newParams?.category_type) {
        setCategoryType(newParams.category_type);
      } else {
        setCategoryType(null);
      }

      if (newParams?.currency) {
        setCurrency(newParams.currency);
      } else {
        setCurrency(null);
      }

      if (newParams?.commission_range && Array.isArray(newParams.commission_range) && newParams.commission_range.length === 2) {
        setCommissionRange(newParams.commission_range.map((v: number) => String(v)));
      } else {
        setCommissionRange(null);
      }

      if (newParams?.price && Array.isArray(newParams.price) && newParams.price.length === 2) {
        setPrice(newParams.price.map((v: number) => String(v)));
      } else {
        setPrice(null);
      }
    },
    [
      filterParams,
      setCategoryId,
      setCategoryType,
      setCurrency,
      setCommissionRange,
      setPrice,
    ]
  );

  const {
    data: marketplace,
    isPending,
    isRefetching,
  } = useQuery({
    queryKey: ['marketplace', searchQuery, filterParams],
    queryFn: async () =>
      await MarketingService.fetchMarketingProductHighlights({
        q: searchQuery || undefined,
        category_type: filterParams?.category_type || undefined,
        category_id: filterParams?.category_id && filterParams.category_id.length > 0
          ? filterParams.category_id
          : undefined,
        commission_min: filterParams?.commission_range && filterParams.commission_range[0] !== 0
          ? filterParams.commission_range[0].toString()
          : undefined,
        commission_max: filterParams?.commission_range && filterParams.commission_range[1] !== 100
          ? filterParams.commission_range[1].toString()
          : undefined,
        price_min: filterParams?.price && filterParams.price[0] !== 0
          ? filterParams.price[0].toString()
          : undefined,
        price_max: filterParams?.price && filterParams.price[1] !== 3000
          ? filterParams.price[1].toString()
          : undefined,
      }),
    staleTime: 1000 * 60 * 60 * 24,
  });

  const handleSearchChange = (value: string) => {
    setLocalSearchValue(value);
  };

  const handleSearch = useCallback(() => {
    setSearchQuery(localSearchValue || null);
  }, [localSearchValue, setSearchQuery]);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const keyLabel = useMemo(
    () => ({
      category_type: t('common:category_type'),
      category_id: t('common:category'),
      currency: t('common:currency'),
      commission_range: t('common:commission_value'),
      price: t('common:product_value'),
    }),
    [t]
  );

  const filterBadgeLabel = useMemo(
    () => ({
      all: t('all'),
      digital: t('common:digital'),
      pharmaceutical: t('common:pharmaceutical'),
    }),
    [t]
  );

  const removeFilter = useCallback(
    (key: string) => {
      const filterSetters = {
        category_id: setCategoryId,
        commission_range: setCommissionRange,
        price: setPrice,
        currency: setCurrency,
        category_type: setCategoryType,
      };

      const setter = filterSetters[key as keyof typeof filterSetters];
      if (setter) {
        setter(null);
      }
    },
    [setCategoryId, setCategoryType, setCurrency, setCommissionRange, setPrice]
  );

  const filterVisualParams = useMemo(() => {
    const convertedParams: any = {
      category_type: filterParams.category_type,
      category_id: filterParams.category_id,
      currency: filterParams.currency,
      commission_range: filterParams.commission_range.map((v: number) => String(v)),
      price: filterParams.price.map((v: number) => String(v)),
    };
    return convertToVisualParams(convertedParams);
  }, [filterParams]);

  const removeFilterItem = useCallback(
    (key: string, index: number) => {
      const filterSetters = {
        category_id: setCategoryId,
        commission_range: setCommissionRange,
        price: setPrice,
        currency: setCurrency,
        category_type: setCategoryType,
      };

      const setter = filterSetters[key as keyof typeof filterSetters];
      if (setter && isFilterKey(key)) {
        const currentValues = filterParams[key];
        if (!Array.isArray(currentValues)) {
          setter(null);
          return;
        }
        const newValues = currentValues.filter((_, i) => i !== index);
        setter((newValues.length ? newValues.map(String) : null) as never);
      }
    },
    [
      filterParams,
      setCategoryId,
      setCategoryType,
      setCurrency,
      setCommissionRange,
      setPrice,
    ]
  );

  const allFilterBadges = useMemo(() => {
    const badges: FilterBadge[] = [];
    const groupLabel = (key: string) =>
      isFilterKey(key) ? keyLabel[key] : key;
    const optionLabel = (value: string) =>
      value in filterBadgeLabel
        ? filterBadgeLabel[value as FilterBadgeLabelKey]
        : value;

    Object.entries(filterVisualParams).forEach(([key, values]) => {
      if (typeof values === 'string' && values === '') return;

      if (
        (key === 'commission_range' || key === 'price') &&
        Array.isArray(values) &&
        values.length === 2
      ) {
        const [min, max] = values;
        const minValue =
          typeof min === 'object' && min !== null ? min.value : min;
        const maxValue =
          typeof max === 'object' && max !== null ? max.value : max;
        const isDefaultRange =
          (key === 'commission_range' &&
            String(minValue) === '0' &&
            String(maxValue) === '100') ||
          (key === 'price' &&
            String(minValue) === '0' &&
            String(maxValue) === '3000');

        if (isDefaultRange) return;
        badges.push({
          key,
          group: `${groupLabel(key)}:`,
          label: `${minValue} - ${maxValue}`,
          onClick: () => removeFilter(key),
        });
      } else if (
        Array.isArray(values) &&
        values.length > 0 &&
        typeof values[0] === 'string'
      ) {
        values.forEach((value, index) => {
          const labelValue = typeof value === 'string' ? value : '';
          badges.push({
            key: `${key}-${index}`,
            group: `${groupLabel(key)}:`,
            label: optionLabel(labelValue) || labelValue,
            onClick: () => removeFilterItem(key, index),
          });
        });
      } else if (typeof values === 'string') {
        badges.push({
          key,
          group: `${groupLabel(key)}:`,
          label: optionLabel(values) || values,
          onClick: () => removeFilter(key),
        });
      }
    });
    return badges;
  }, [
    filterVisualParams,
    keyLabel,
    filterBadgeLabel,
    removeFilterItem,
    removeFilter,
  ]);

  const clearAllFilters = useCallback(() => {
    setCategoryId(null);
    setCategoryType(null);
    setCurrency(null);
    setCommissionRange(null);
    setPrice(null);
    setSearchQuery(null);
    setLocalSearchValue('');
  }, [
    setCategoryId,
    setCategoryType,
    setCurrency,
    setCommissionRange,
    setPrice,
    setSearchQuery,
  ]);

  return (
    <LayoutInside items={items}>
      <CustomSheet
        onClose={() => closeModal('market-filter')}
        open={modals['market-filter']}
      >
        <MarketFilter
          filterParams={filterParams}
          onFilter={(data) => setFilterParams(data)}
        />
      </CustomSheet>
      <Card className='flex flex-col p-4 md:flex-row gap-2 md:gap-4 justify-between items-start md:items-center my-4'>
        <div className='flex gap-2 w-full max-w-[480px]'>
          <Command className='flex-1'>
            <CommandInput
              value={localSearchValue}
              placeholder={t('common:search_product')}
              onValueChange={handleSearchChange}
              onKeyDown={handleKeyPress}
              data-cy='search-sales'
            />
          </Command>
          <Button
            variant='default'
            onClick={handleSearch}
            data-cy='search-button'
            className='px-4'
          >
            <Search size={20} />
          </Button>
        </div>

        <Button
          variant='secondary'
          onClick={() => openModal('market-filter')}
          data-cy='open-filter-button'
        >
          <ListFilter size={24} />
          {t('sales:filter')}
        </Button>
      </Card>

      <div className='flex flex-wrap gap-2 mt-4'>
        {allFilterBadges.slice(0, 10).map((badge) => (
          <Badge
            key={badge.key}
            className='flex font-medium min-h-7 items-center hover:opacity-80 hover:bg-[#18181b] gap-4 bg-[#18181b] text-colorText'
          >
            <div className='flex text-colorText items-center gap-1'>
              <span className='text-muted'>{badge.group}</span>
              {badge.label}
            </div>
            <button
              onClick={badge.onClick}
              className='text-muted cursor-pointer'
            >
              ✕
            </button>
          </Badge>
        ))}

        {allFilterBadges.length > 0 && (
          <Button
            variant='secondary'
            onClick={clearAllFilters}
            data-cy='clear-filters'
            className='ml-2 h-7 flex items-center gap-4 bg-transparent text-interactive'
          >
            {t('common:clear_all_filters')}
          </Button>
        )}
      </div>

      <section className='mt-12'>
        <div className='flex sm:flex-row flex-col sm:items-center gap-4 mb-12'>
          <RocketBlock />
          <article className='flex flex-col'>
            <h2 className='text-xl font-semibold'>{t('top10')}</h2>
            <p className='text-muted-foreground'>{t('top10description')}</p>
          </article>
        </div>
        {isPending || isRefetching ? (
          <div className='mt-12 grid grid-cols-1 gap-y-8 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6'>
            {Array.from({ length: 8 }).map((_, index) => (
              <Skeleton key={index} className='w-full h-[470px] rounded-md' />
            ))}
          </div>
        ) : marketplace?.top_selling?.length === 0 ? (
          <EmptyItem
            title={t('emptyToSellingTitle')}
            description={t('emptyToSellingDescription')}
            icon={<RocketBlock />}
          />
        ) : (
          <div className='grid grid-cols-1 gap-y-8 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6'>
            {marketplace?.top_selling?.map((item: MarketplaceProductListItem, idx: number) => (
              <MarketingCard
                key={item.uuid}
                title={item.name}
                description={item.description}
                amount={item.commission_formatted}
                productId={item.uuid}
                index={idx}
                images={item.images}
                commissions={item.offers_grouped}
                isTopSelling
              />
            ))}
          </div>
        )}
      </section>

      <Divider className='my-16' />

      <section>
        <div className='flex sm:flex-row flex-col sm:items-center gap-4 mb-8'>
          <FireFlameBlock />
          <article className='flex flex-col'>
            <h2 className='text-xl font-semibold'>{t('trending')}</h2>
            <p className='text-muted-foreground'>{t('trendingdescription')}</p>
          </article>
        </div>
        {!isPending && marketplace?.trending?.length === 0 ? (
          <EmptyItem
            title={t('emptyTrendingTitle')}
            description={t('emptyTrendingDescription')}
            icon={<FireFlameBlock />}
          />
        ) : (
          <Swiper
            modules={[Navigation]}
            navigation
            spaceBetween={24}
            slidesPerView={1.2}
            breakpoints={{
              640: { slidesPerView: 2.2 },
              900: { slidesPerView: 3.2 },
              1200: { slidesPerView: 3.6 },
            }}
            className='pb-10'
          >
            {marketplace?.trending?.map((item: MarketplaceProductListItem, idx: number) => (
              <SwiperSlide key={idx}>
                <MarketingCard
                  key={item.uuid}
                  title={item.name}
                  description={item.description}
                  amount={item.commission_formatted}
                  productId={item.uuid}
                  index={idx}
                  commissions={item.offers_grouped}
                  isTopSelling={false}
                  images={item.images}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        )}
      </section>

      <Divider className='my-16' />

      <section>
        <div className='flex flex-col sm:flex-row sm:items-center gap-4 mb-8'>
          <SpaarklesBlock />
          <article className='flex flex-col'>
            <h2 className='text-xl font-semibold'>{t('recent')}</h2>
            <p className='text-muted-foreground'>{t('recentdescription')}</p>
          </article>
        </div>
        {!isPending && marketplace?.recent?.length === 0 ? (
          <EmptyItem
            title={t('emptyRecentTitle')}
            description={t('emptyRecentDescription')}
            icon={<SpaarklesBlock />}
          />
        ) : (
          <Swiper
            modules={[Navigation]}
            navigation
            spaceBetween={32}
            slidesPerView={1.2}
            breakpoints={{
              640: { slidesPerView: 2.2 },
              900: { slidesPerView: 3.2 },
              1200: { slidesPerView: 3.6 },
            }}
            className='pb-10'
          >
            {(marketplace?.recent ?? []).map((item: MarketplaceProductListItem, idx: number) => (
              <SwiperSlide key={idx}>
                <MarketingCard
                  key={item.uuid}
                  title={item.name}
                  description={item.description}
                  amount={item.commission_formatted}
                  productId={item.uuid}
                  index={idx}
                  isTopSelling={false}
                  commissions={item.offers_grouped}
                  images={item.images}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        )}
      </section>
    </LayoutInside>
  );
}
